if (!customElements.get('silhouette-card')) {
    class SilhouetteCard extends HTMLElement {
        rovingIndex = 0;
        controller = new AbortController();
        mouseMoveController = null;
        get button() {
            return this.querySelector('.silhouette-grid__trigger');
        }
        get productCards() {
            return Array.from(this.querySelectorAll('.silhouette-grid__card'));
        }
        connectedCallback() {
            const { signal } = this.controller;
            this.addEventListener('mouseenter', this.onMouseEnter, { signal });
            this.addEventListener('mouseleave', this.onMouseLeave, { signal });
            this.addEventListener('keydown', this.onKeyDown, { signal });
            this.addEventListener('focusin', this.onFocusIn, { signal });
            this.addEventListener('focusout', this.onFocusOut, { signal });
            this.button?.addEventListener('click', this.onButtonClick, { signal });
        }
        disconnectedCallback() {
            this.controller.abort();
            this.controller = new AbortController();
            this.mouseMoveController?.abort();
            this.mouseMoveController = null;
        }
        get deadZonePx() {
            const grid = this.closest('.silhouette-grid');
            return Number(grid?.dataset.deadZone ?? 32);
        }
        isInDeadZone(event) {
            if (!window.matchMedia('(min-width: 990px)').matches)
                return false;
            const rect = this.getBoundingClientRect();
            const x = event.clientX - rect.left;
            return x < this.deadZonePx || x > rect.width - this.deadZonePx;
        }
        onMouseEnter = (event) => {
            if (!window.matchMedia('(hover: hover)').matches)
                return;
            if (this.isInDeadZone(event)) {
                this.dataset.inDeadZone = 'true';
                this.mouseMoveController = new AbortController();
                this.addEventListener('mousemove', this.onMouseMoveInDeadZone, {
                    signal: this.mouseMoveController.signal,
                });
                return;
            }
            this.untoggleSiblings();
            this.setActive(true);
        };
        onMouseMoveInDeadZone = (event) => {
            if (this.isInDeadZone(event))
                return;
            delete this.dataset.inDeadZone;
            this.mouseMoveController?.abort();
            this.mouseMoveController = null;
            this.untoggleSiblings();
            this.setActive(true);
        };
        onMouseLeave = () => {
            if (!window.matchMedia('(hover: hover)').matches)
                return;
            delete this.dataset.inDeadZone;
            this.mouseMoveController?.abort();
            this.mouseMoveController = null;
            if (!this.contains(document.activeElement)) {
                this.setActive(false);
            }
        };
        onButtonClick = () => {
            const isActive = this.dataset.active === 'true';
            if (!isActive)
                this.untoggleSiblings();
            this.setActive(!isActive);
        };
        onKeyDown = (event) => {
            const cards = this.productCards;
            if (this.dataset.active !== 'true' || cards.length === 0)
                return;
            switch (event.key) {
                case 'ArrowRight':
                    event.preventDefault();
                    this.rovingIndex = (this.rovingIndex + 1) % cards.length;
                    this.updateRovingTabindex();
                    cards[this.rovingIndex].focus();
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    this.rovingIndex = (this.rovingIndex - 1 + cards.length) % cards.length;
                    this.updateRovingTabindex();
                    cards[this.rovingIndex].focus();
                    break;
                case 'Escape':
                    this.setActive(false);
                    this.button?.focus();
                    break;
            }
        };
        // Sync rovingIndex when focus lands on a card (e.g. via mouse click)
        onFocusIn = (event) => {
            const index = this.productCards.indexOf(event.target);
            if (index !== -1) {
                this.rovingIndex = index;
                this.updateRovingTabindex();
            }
        };
        // Collapse when focus leaves the component entirely
        onFocusOut = (event) => {
            const related = event.relatedTarget;
            if (!related || !this.contains(related)) {
                this.setActive(false);
            }
        };
        setActive(active) {
            this.dataset.active = String(active);
            if (this.button)
                this.button.ariaExpanded = String(active);
            const cards = this.productCards;
            if (active) {
                this.rovingIndex = 0;
                this.updateRovingTabindex();
            }
            else {
                cards.forEach((card) => (card.tabIndex = -1));
                this.rovingIndex = 0;
            }
        }
        updateRovingTabindex() {
            this.productCards.forEach((card, i) => {
                card.tabIndex = i === this.rovingIndex ? 0 : -1;
            });
        }
        untoggleSiblings() {
            document.querySelectorAll('silhouette-card').forEach((sibling) => {
                if (sibling !== this)
                    sibling.setActive(false);
            });
        }
    }
    customElements.define('silhouette-card', SilhouetteCard);
}
